/**
 * Format date to Indian format: dd/mm/yyyy hh:mm AM/PM (IST)
 * Handles both ISO strings and already-IST timestamps
 */
export const formatIndianDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  let date;
  
  // Check if the date string includes timezone info
  if (typeof dateString === 'string') {
    // If it's a UTC timestamp (ends with Z or +00:00), convert to IST
    if (dateString.endsWith('Z') || dateString.includes('+00:00')) {
      // Parse as UTC and convert to IST
      const utcDate = new Date(dateString);
      // Add 5:30 hours for IST
      date = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
    } else if (dateString.includes('+05:30')) {
      // Already in IST, parse directly
      date = new Date(dateString);
    } else {
      // No timezone info, assume it's IST
      date = new Date(dateString);
    }
  } else {
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) return 'N/A';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const formattedHours = String(hours).padStart(2, '0');
  
  return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
};

/**
 * Format time only: hh:mm AM/PM (IST)
 */
export const formatIndianTime = (timeString) => {
  if (!timeString) return 'N/A';
  
  // If it's just a time string like "14:30:00"
  if (timeString.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
    const [hours, minutes] = timeString.split(':');
    let h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12;
    return `${String(h).padStart(2, '0')}:${minutes} ${ampm}`;
  }
  
  // Otherwise, treat as full datetime
  return formatIndianDate(timeString);
};

/**
 * Format date only (without time): dd/mm/yyyy
 */
export const formatIndianDateOnly = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) return 'N/A';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Get status badge variant based on status
 */
export const getStatusVariant = (status) => {
  if (!status) return 'default';
  
  const statusLower = status.toLowerCase();
  
  // Active/Success states
  if (statusLower === 'active' || statusLower === 'success') {
    return 'default'; // Green
  }
  
  // Inactive/Failed states
  if (statusLower === 'inactive' || statusLower === 'failed') {
    return 'destructive'; // Red
  }
  
  // In Progress/On Progress
  if (statusLower === 'on progress' || statusLower === 'in progress') {
    return 'secondary'; // Yellow/Orange
  }
  
  // New/Pending
  if (statusLower === 'new' || statusLower === 'pending') {
    return 'outline'; // Blue outline
  }
  
  // Postponed
  if (statusLower === 'postponed') {
    return 'outline'; // Gray outline
  }
  
  return 'default';
};

/**
 * Get status color class for badges
 */
export const getStatusColorClass = (status) => {
  if (!status) return '';
  
  const statusLower = status.toLowerCase();
  
  // Active/Success - Green
  if (statusLower === 'active' || statusLower === 'success') {
    return 'bg-green-100 text-green-800 border-green-300';
  }
  
  // Inactive/Failed - Red
  if (statusLower === 'inactive' || statusLower === 'failed') {
    return 'bg-red-100 text-red-800 border-red-300';
  }
  
  // On Progress - Orange
  if (statusLower === 'on progress' || statusLower === 'in progress') {
    return 'bg-orange-100 text-orange-800 border-orange-300';
  }
  
  // New - Blue
  if (statusLower === 'new') {
    return 'bg-blue-100 text-blue-800 border-blue-300';
  }
  
  // Postponed - Gray
  if (statusLower === 'postponed') {
    return 'bg-gray-100 text-gray-800 border-gray-300';
  }
  
  return '';
};
